import { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import BottomSheet from '../shared/BottomSheet';

/**
 * Bottom sheet para elegir un valor de rango (radio buttons).
 *
 * Props:
 *   isOpen, onClose
 *   title: string (ej "Compra mínima")
 *   options: [{ value, label }]
 *   currentValue: valor seleccionado actual (number | null)
 *   onApply: (newValue) => void
 *
 * Usa el BottomSheet shared — body scroll lock + animación + backdrop
 * + estructura consistente con el resto de sheets del proyecto.
 */
export default function RangeFilterSheet({ isOpen, onClose, title, options, currentValue, onApply }) {
  const [draft, setDraft] = useState(currentValue);

  useEffect(() => {
    if (isOpen) setDraft(currentValue);
  }, [isOpen, currentValue]);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClear = () => {
    onApply(null);
    onClose();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} zIndex={60}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <h2 className="text-xl font-bold font-display text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Cerrar"
        >
          <X size={18} weight="bold" className="text-gray-700" />
        </button>
      </div>

      {/* Body: opciones radio */}
      <div className="flex flex-col gap-1 px-5 py-5 border-t border-gray-100">
        {options.map((opt) => {
          const isSelected = draft === opt.value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => setDraft(opt.value)}
              className="flex items-center gap-3 py-3 text-left active:scale-[0.99] transition-transform"
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'border-cta-bg' : 'border-gray-300'
                }`}
              >
                {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-cta-bg" />}
              </span>
              <span className={`text-base font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div
        className="flex gap-3 px-5 pt-3 border-t border-gray-100"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <button
          type="button"
          onClick={handleClear}
          className="flex-1 py-3 text-sm font-bold text-gray-700 active:scale-[0.98] transition-transform"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={draft == null}
          className="flex-1 bg-cta-bg hover:bg-cta-hover text-white rounded-full py-3 text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          Aplicar
        </button>
      </div>
    </BottomSheet>
  );
}
